import ArrowRightIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import CanAcknowledgeIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import {
  Avatar,
  Box,
  IconButton,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getInitials } from "../../../common/helpers/getInitials";
import HrTaskContentRows from "./HrTaskContentRows";
import { resolveLogicalLinkRoute } from "./hrTaskLinks";
import { HrTask } from "./types";

type Props = {
  task: HrTask;
  isLast: boolean;
  handleSelectHrTask: (taskId: string) => void;
};

const HrTaskListItem = ({ task, isLast, handleSelectHrTask }: Props) => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const title = task.presentation.title;
  const defaultAction = task.actions.defaultAction;
  const to = defaultAction
    ? resolveLogicalLinkRoute(defaultAction.link.target)
    : undefined;
  const canAcknowledge = task.canManuallyAcknowledge;

  const openLinkedView = (event: MouseEvent) => {
    event.stopPropagation();
    if (!to || !defaultAction) return;
    if (defaultAction.link.openInNewTab) {
      window.open(to, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(to);
  };

  return (
    <Box
      data-testid="hr-task-card"
      display="flex"
      alignItems="center"
      onClick={canAcknowledge ? () => handleSelectHrTask(task.id) : undefined}
      sx={{
        minWidth: 0,
        bgcolor: "#fff",
        marginBottom: isLast ? "0px" : "16px",
        borderRadius: "8px",
        cursor: canAcknowledge ? "pointer" : "default",
        padding: { xs: "4px 8px", xl: "6px 10px" },
      }}
    >
      <CanAcknowledgeIcon
        data-testid="hr-task-acknowledge"
        fontSize="small"
        sx={{
          color: (theme) => theme.palette.primary.light,
          visibility: canAcknowledge ? "visible" : "hidden",
          marginRight: { xs: "8px", xl: "10px" },
          width: "16px",
          height: "16px",
        }}
      />
      <ListItemAvatar sx={{ minWidth: { xs: "48px", xl: "54px" } }}>
        <Avatar sx={{ width: "34px", height: "34px" }}>
          {getInitials(title)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        sx={{ minWidth: 0, overflowWrap: "anywhere" }}
        primaryTypographyProps={{
          fontWeight: 600,
          variant: "header",
          fontSize: "14px",
        }}
        primary={title}
        secondary={
          <HrTaskContentRows rows={task.presentation.rows} variant="body2" />
        }
        secondaryTypographyProps={{ component: "div" }}
      />
      {to && (
        <IconButton
          size="small"
          aria-label={t("common:open")}
          onClick={openLinkedView}
          sx={{
            width: { xs: "30px", xl: "34px" },
            height: { xs: "30px", xl: "34px" },
          }}
        >
          <ArrowRightIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default HrTaskListItem;
